import { useState, useEffect, useRef, useCallback } from "react";
import { FileDown, Loader2, Redo2, Square, Undo2 } from "lucide-react";

import {
  applyReportEdit,
  getReport,
  previewReportEdit,
  redoReport,
  rejectReportEdit,
  streamReportChat,
  undoReport,
} from "../../../api/reports";
import { scrapePublicCitation } from "../../../api/chat";

import { useRightSidebar } from "../../contexts/RightSidebarContext";
import { useCompanyContext } from "../../../context";
import ArticleDetailPanel from "../../Universal/ArticleCardDetailPanel";
import CitationLoadingAnimation from "../../Universal/CitationLoadingAnimation";
import { getTheme } from "../../Universal/useNavTheme";

import ReportDocument from "./ReportDocument";
import ReportEditSelectionModal from "./ReportEditSelectionModal";
import DefaultReportView from "./DefaultReportView";
import DefaultReportViewII from "./DefaultReportViewII";
import { buildReportPrintHtml } from "./reportPrintHtml";
import { getUserDatetime } from "./utils/reportUtils";

const isII = process.env.REACT_APP_IS_II;

const ReportInput = ({ onSubmit }) => {
  const [value, setValue] = useState("");
  const textareaRef = useRef(null);

  useEffect(() => {
    textareaRef.current?.focus();
  }, []);

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      if (value.trim()) onSubmit(value.trim());
    }
  };

  const canSubmit = value.trim().length > 0;

  return (
    <div className="h-full flex flex-col items-center justify-center px-6 pt-14">
      <div className="w-full max-w-[600px]">
        <h2 className="text-[13px] font-medium text-mainText text-center mb-4">
          Describe the report you want in detail
        </h2>
        <div className="rounded-xl border border-surfaceBorder bg-reortTileBg overflow-hidden">
          <textarea
            ref={textareaRef}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="e.g. Write a competitive analysis of the top 5 CRM platforms, focusing on pricing, features, and market share..."
            rows={5}
            className="w-full resize-none bg-transparent text-[13.5px] leading-relaxed px-4 pt-4 pb-2 text-mainText placeholder:text-mutedText focus:outline-none"
          />
          <div className="flex justify-end px-3 pb-3">
            <button
              onClick={() => canSubmit && onSubmit(value.trim())}
              disabled={!canSubmit}
              className="px-3 py-1.5 rounded-md text-[11px] font-medium text-mutedText hover:text-mainText border border-surfaceBorder hover:bg-mainButtonBg/50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:text-mutedText disabled:hover:bg-transparent"
            >
              Generate Report
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════════════════
   Reusable loading panel
   ═══════════════════════════════════════════════════════════════════════════ */

const reportMutationError = (res, data, fallback) => {
  const base = data?.error || fallback;
  if (res?.status === 404) {
    return `${base} The report edit endpoint was not found. Restart or update the backend, then reload the report.`;
  }
  if (res?.status === 409) {
    return `${base} Reload the report, then reselect the text and try again.`;
  }
  return base;
};

const LoadingPanel = ({ message, onStop }) => (
  <div className="h-full flex flex-col items-center justify-center px-6 pt-14">
    <div className="flex items-center gap-2.5 px-4 py-2.5 rounded-xl border border-surfaceBorder bg-reortTileBg">
      <Loader2
        className="w-3.5 h-3.5 animate-spin text-mutedText"
        strokeWidth={2}
      />
      <span className="text-[12px] text-mainText">{message}</span>
      {onStop && (
        <button
          onClick={onStop}
          className="ml-2 flex items-center gap-1 px-2 py-0.5 rounded-md text-[10.5px] font-medium text-mutedText hover:text-mainText border border-surfaceBorder hover:bg-mainButtonBg/50 transition-colors"
        >
          <Square className="w-2.5 h-2.5" strokeWidth={2.5} />
          Stop
        </button>
      )}
    </div>
  </div>
);

/* ═══════════════════════════════════════════════════════════════════════════
   MainReports — orchestrator
   ═══════════════════════════════════════════════════════════════════════════ */

const MainReports = () => {
  const [reportId, setReportId] = useState(null);
  const [reportTitle, setReportTitle] = useState("Untitled Report");
  const [reportContent, setReportContent] = useState("");
  const [reportCitations, setReportCitations] = useState([]);
  const [reportOptions, setReportOptions] = useState({});
  const [revision, setRevision] = useState(0);
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);
  const [chatHistory, setChatHistory] = useState([]);
  const [researchSteps, setResearchSteps] = useState([]);
  const [streamingReply, setStreamingReply] = useState("");
  const [reportError, setReportError] = useState("");
  const [selectedEdit, setSelectedEdit] = useState(null);
  const [editSuggestion, setEditSuggestion] = useState(null);

  const [isLoadingReport, setIsLoadingReport] = useState(false);

  const [isStreaming, setIsStreaming] = useState(false);
  const [isUndoingReport, setIsUndoingReport] = useState(false);
  const [isRedoingReport, setIsRedoingReport] = useState(false);
  const [isPreviewingEdit, setIsPreviewingEdit] = useState(false);
  const [isApplyingEdit, setIsApplyingEdit] = useState(false);
  const [isRejectingEdit, setIsRejectingEdit] = useState(false);
  const [streamingReportContent, setStreamingReportContent] = useState("");
  const [statusText, setStatusText] = useState("");
  const [hasSubmitted, setHasSubmitted] = useState(false);

  const abortRef = useRef(null);
  const controlBufRef = useRef("");
  const fullReplaceAnimRef = useRef(null);
  const streamingContentRef = useRef("");
  const researchStepsRef = useRef([]);
  const didMutateReportRef = useRef(false);

  const {
    isRightOpen,
    setIsRightOpen,
    setRightComponent,
    setRightSidebarMode,
  } = useRightSidebar();
  const { currentCompany } = useCompanyContext();
  const [activeCitationUrl, setActiveCitationUrl] = useState(null);
  const [citationInteractions, setCitationInteractions] = useState({});

  useEffect(() => {
    return () => {
      abortRef.current?.abort();
      if (fullReplaceAnimRef.current) {
        clearInterval(fullReplaceAnimRef.current);
        fullReplaceAnimRef.current = null;
      }
    };
  }, []);

  const handleCitationClick = useCallback(
    (citation) => {
      if (!citation.url) return;
      const citationKey = citation.url;
      if (activeCitationUrl === citationKey && isRightOpen) return;
      setActiveCitationUrl(citationKey);
      setRightSidebarMode(null);
      setRightComponent(
        <div className="h-full">
          <CitationLoadingAnimation />
        </div>,
      );
      setIsRightOpen(true);

      scrapePublicCitation({ url: citation.url })
        .then((res) => {
          const data = res?.data;
          if (!data?.success && !data?.already_saved) {
            setRightComponent(
              <div className="p-6 text-center text-[13px] text-mutedText">
                Unable to load source
              </div>,
            );
            return;
          }
          const dark = document.documentElement.classList.contains("dark");
          const navT = getTheme(dark);
          if (data.like || data.dislike || data.catalog) {
            setCitationInteractions((prev) => ({
              ...prev,
              [citationKey]: {
                like: !!data.like,
                dislike: !!data.dislike,
                catalog: !!data.catalog,
              },
            }));
          }
          const article = {
            document_id: data.doc_id || citationKey,
            title: data.title || data.domain || citation.title || "Untitled",
            url: data.url || citation.url,
            source: data.source || data.domain || "",
            content: data.content || "",
            description: data.description || "",
            screenshot: data.screenshot || "",
            screenshot_full: data.screenshot_full || "",
            target_type: data.target_type || "web_citation",
            timestamp: data.date_published
              ? new Date(data.date_published).toLocaleDateString(undefined, {
                  year: "numeric",
                  month: "short",
                  day: "numeric",
                })
              : "",
            like: !!data.like,
            dislike: !!data.dislike,
            catalog: !!data.catalog,
          };
          const onInteract = (_cardId, _docId, state) => {
            setCitationInteractions((prev) => ({
              ...prev,
              [citationKey]: state,
            }));
          };
          setRightComponent(
            <CitationLoadingAnimation
              isComplete={true}
              statusMessage="Source captured"
              onCompleteFinished={() => {
                setRightComponent(
                  <ArticleDetailPanel
                    article={article}
                    cardId={article.document_id}
                    t={navT}
                    onInteract={onInteract}
                  />,
                );
              }}
            />,
          );
        })
        .catch(() => {
          setRightComponent(
            <div className="p-6 text-center text-[13px] text-mutedText">
              Unable to load source
            </div>,
          );
        });
    },
    [
      activeCitationUrl,
      isRightOpen,
      setIsRightOpen,
      setRightComponent,
      setRightSidebarMode,
    ],
  );

  useEffect(() => {
    if (!isRightOpen) setActiveCitationUrl(null);
  }, [isRightOpen]);

  useEffect(() => {
    const handler = (e) => {
      const id = e.detail?.reportId;
      if (!id) return;

      abortRef.current?.abort();
      abortRef.current = null;
      if (fullReplaceAnimRef.current) {
        clearInterval(fullReplaceAnimRef.current);
        fullReplaceAnimRef.current = null;
      }

      setReportId(id);

      if (id !== "default") {
        setIsLoadingReport(true);
        setReportTitle("Untitled Report");
        setReportContent("");
        setReportCitations([]);
        setReportOptions({});
        setRevision(0);
        setCanUndo(false);
        setCanRedo(false);
        setChatHistory([]);
        setResearchSteps([]);
        researchStepsRef.current = [];
        setStreamingReply("");
        setReportError("");
        setSelectedEdit(null);
        setEditSuggestion(null);
        setHasSubmitted(false);
        setCitationInteractions({});
      } else {
        setIsLoadingReport(false);
        setRevision(0);
        setCanUndo(false);
        setCanRedo(false);
        setChatHistory([]);
        setResearchSteps([]);
        researchStepsRef.current = [];
        setStreamingReply("");
        setReportError("");
        setSelectedEdit(null);
        setEditSuggestion(null);
      }

      setIsStreaming(false);
      setIsUndoingReport(false);
      setIsRedoingReport(false);
      setStreamingReportContent("");
      streamingContentRef.current = "";
      setStatusText("");
      controlBufRef.current = "";
      setIsRightOpen(false);
      setActiveCitationUrl(null);
    };
    window.addEventListener("sidebar:report-selected", handler);
    return () => window.removeEventListener("sidebar:report-selected", handler);
  }, [setIsRightOpen]);

  useEffect(() => {
    const handler = (e) => {
      const id = e.detail?.reportId;
      if (!id) return;

      abortRef.current?.abort();
      abortRef.current = null;
      if (fullReplaceAnimRef.current) {
        clearInterval(fullReplaceAnimRef.current);
        fullReplaceAnimRef.current = null;
      }

      setReportId(id);
      setIsLoadingReport(false);
      setReportTitle("Untitled Report");
      setReportContent("");
      setReportCitations([]);
      setReportOptions({});
      setRevision(0);
      setCanUndo(false);
      setCanRedo(false);
      setChatHistory([]);
      setResearchSteps([]);
      researchStepsRef.current = [];
      setStreamingReply("");
      setReportError("");
      setSelectedEdit(null);
      setEditSuggestion(null);
      setHasSubmitted(false);
      setCitationInteractions({});
      setIsStreaming(false);
      setIsUndoingReport(false);
      setIsRedoingReport(false);
      setStreamingReportContent("");
      streamingContentRef.current = "";
      setStatusText("");
      controlBufRef.current = "";
      setIsRightOpen(false);
      setActiveCitationUrl(null);
    };
    window.addEventListener("sidebar:new-report", handler);
    return () => window.removeEventListener("sidebar:new-report", handler);
  }, [setIsRightOpen]);

  useEffect(() => {
    if (reportId)
      window.dispatchEvent(
        new CustomEvent("sidebar:select-report", { detail: { reportId } }),
      );
  }, [reportId]);

  useEffect(() => {
    if (!reportId || reportId === "default") return;
    let cancelled = false;
    getReport(reportId)
      .then((res) => {
        const doc = res?.data || {};
        if (cancelled || doc.error) return;
        setReportTitle(doc.title || "Untitled Report");
        const content = doc.report_content || "";
        setReportContent(content);
        setReportCitations(doc.report_citations || []);
        setReportOptions(doc.report_options || {});
        setRevision(
          Number.isFinite(Number(doc.revision)) ? Number(doc.revision) : 0,
        );
        setCanUndo(Boolean(doc.can_undo));
        setCanRedo(Boolean(doc.can_redo));
        setChatHistory(Array.isArray(doc.chat_history) ? doc.chat_history : []);
        setResearchSteps([]);
        researchStepsRef.current = [];
        setStreamingReply("");
        setReportError("");
        setSelectedEdit(null);
        setEditSuggestion(null);
        setHasSubmitted(content.trim().length > 0);

        const citations = doc.report_citations || [];
        if (citations.length > 0) {
          const interactions = {};
          let changed = false;
          Promise.allSettled(
            citations
              .filter((c) => c.url)
              .map((c) =>
                scrapePublicCitation({ url: c.url }).then((r) => {
                  if (cancelled) return;
                  const d = r?.data;
                  if (d && (d.like || d.dislike || d.catalog)) {
                    interactions[c.url] = {
                      like: !!d.like,
                      dislike: !!d.dislike,
                      catalog: !!d.catalog,
                    };
                    changed = true;
                  }
                }),
              ),
          ).then(() => {
            if (!cancelled && changed)
              setCitationInteractions((prev) => ({ ...prev, ...interactions }));
          });
        }
      })
      .catch((err) => {
        if (!cancelled) console.error("Failed to load report:", err);
      })
      .finally(() => {
        if (!cancelled) setIsLoadingReport(false);
      });
    return () => {
      cancelled = true;
    };
  }, [reportId]);

  const isControlStart = (s) =>
    s.startsWith('{"report_action"') ||
    s.startsWith('{"status"') ||
    s.startsWith('{"error"') ||
    s.startsWith('{"research_step"');

  const recordResearchStep = useCallback((step) => {
    const text = (step?.text || "").trim();
    if (!text) return;
    const normalized = { ...step, text };
    const byText = new Map(
      researchStepsRef.current.map((existing) => [
        (existing.text || "").trim(),
        existing,
      ]),
    );
    byText.set(text, normalized);
    const next = Array.from(byText.values());
    researchStepsRef.current = next;
    setResearchSteps(next);
  }, []);

  const tryConsumeControl = useCallback(
    (maybeJson) => {
      const trimmed = maybeJson.trim();
      if (!isControlStart(trimmed)) return { consumed: false };
      try {
        const obj = JSON.parse(trimmed);
        if (obj.error) {
          console.error("[reports/chat] Stream error:", obj.error);
          setReportError(obj.error);
          return { consumed: true };
        }
        if (obj.status) {
          setStatusText(obj.status);
          setReportError("");
          return { consumed: true };
        }
        if (obj.research_step) {
          const step = obj.research_step;
          const text = (step.text || "").trim();
          if (text) {
            setStatusText((step.status === "done" ? "✓ " : "") + text);
            recordResearchStep(step);
          }
          return { consumed: true };
        }
        switch (obj.report_action) {
          case "stream_start":
            if (obj.title) setReportTitle(obj.title);
            streamingContentRef.current = "";
            setStreamingReportContent("");
            break;
          case "stream_chunk": {
            const next = streamingContentRef.current + (obj.content || "");
            streamingContentRef.current = next;
            setStreamingReportContent(next);
            break;
          }
          case "stream_done": {
            const finalContent = streamingContentRef.current;
            setReportContent(finalContent);
            didMutateReportRef.current = true;
            const titleMatch = finalContent.match(/^#\s+(.+)$/m);
            if (titleMatch?.[1]?.trim()) setReportTitle(titleMatch[1].trim());
            streamingContentRef.current = "";
            setStreamingReportContent("");
            break;
          }
          case "full_replace":
            if (obj.title) setReportTitle(obj.title);
            if (obj.content !== undefined) {
              if (fullReplaceAnimRef.current) {
                clearInterval(fullReplaceAnimRef.current);
                fullReplaceAnimRef.current = null;
              }
              const fullContent = obj.content;
              const chunkSize = 150;
              let i = 0;
              streamingContentRef.current = "";
              setStreamingReportContent("");
              fullReplaceAnimRef.current = setInterval(() => {
                i += chunkSize;
                if (i >= fullContent.length) {
                  clearInterval(fullReplaceAnimRef.current);
                  fullReplaceAnimRef.current = null;
                  streamingContentRef.current = "";
                  setStreamingReportContent("");
                  setReportContent(fullContent);
                  didMutateReportRef.current = true;
                } else {
                  const slice = fullContent.slice(0, i);
                  streamingContentRef.current = slice;
                  setStreamingReportContent(slice);
                }
              }, 12);
            }
            break;
          case "citations":
            if (obj.citations) setReportCitations(obj.citations);
            break;
          case "set_option": {
            const { report_action: _, ...rest } = obj;
            setReportOptions((prev) => ({ ...prev, ...rest }));
            didMutateReportRef.current = true;
            break;
          }
          case "saved":
            if (Number.isFinite(Number(obj.revision)))
              setRevision(Number(obj.revision));
            if (didMutateReportRef.current) {
              setCanUndo(true);
              setCanRedo(false);
            }
            setIsStreaming(false);
            setReportError("");
            window.dispatchEvent(new CustomEvent("sidebar:refresh-reports"));
            break;
          default:
            break;
        }
        return { consumed: true };
      } catch {
        return { consumed: false, needsBuffer: true };
      }
    },
    [recordResearchStep],
  );

  const handleSend = useCallback(
    async (message) => {
      if (!message.trim() || !reportId || isStreaming) return;
      const hasReport = reportContent && reportContent.trim().length > 0;
      const msg = hasReport
        ? message.trim()
        : message.trim() +
          "\n\nWrite the report now. Go ahead and generate the report.";
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;
      if (fullReplaceAnimRef.current) {
        clearInterval(fullReplaceAnimRef.current);
        fullReplaceAnimRef.current = null;
      }
      setIsStreaming(true);
      setHasSubmitted(true);
      streamingContentRef.current = "";
      setStreamingReportContent("");
      setStatusText("");
      setStreamingReply("");
      setReportError("");
      setSelectedEdit(null);
      setEditSuggestion(null);
      if (!hasReport) setIsRightOpen(false);
      researchStepsRef.current = [];
      didMutateReportRef.current = false;
      setResearchSteps([]);
      controlBufRef.current = "";
      setChatHistory((prev) => [
        ...prev,
        {
          role: "user",
          content: message.trim(),
          timestamp: new Date().toISOString(),
        },
      ]);
      try {
        const response = await streamReportChat({
          reportId,
          message: msg,
          userDatetime: getUserDatetime(),
          signal: controller.signal,
          target: currentCompany?.target,
        });
        if (!response.ok || !response.body)
          throw new Error(`HTTP error: ${response.status}`);
        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let chatOutput = "";
        let streamBuffer = "";
        const appendChatText = (text) => {
          if (!text) return;
          chatOutput += text;
          setStreamingReply(chatOutput.trimStart());
        };
        const findNextControlStart = (value) => {
          const needles = [
            '{"report_action"',
            '{"status"',
            '{"error"',
            '{"research_step"',
          ];
          return needles.reduce((best, needle) => {
            const index = value.indexOf(needle);
            if (index < 0) return best;
            return best < 0 ? index : Math.min(best, index);
          }, -1);
        };
        const tryExtractJsonObject = (value, start) => {
          let depth = 0;
          let inString = false;
          let escaped = false;
          for (let index = start; index < value.length; index += 1) {
            const char = value[index];
            if (inString) {
              if (escaped) escaped = false;
              else if (char === "\\") escaped = true;
              else if (char === '"') inString = false;
              continue;
            }
            if (char === '"') inString = true;
            else if (char === "{") depth += 1;
            else if (char === "}") {
              depth -= 1;
              if (depth === 0) return value.slice(start, index + 1);
            }
          }
          return null;
        };
        const processChunk = (raw) => {
          streamBuffer += raw;
          while (streamBuffer) {
            const controlStart = findNextControlStart(streamBuffer);
            if (controlStart < 0) {
              appendChatText(streamBuffer);
              streamBuffer = "";
              return;
            }
            if (controlStart > 0) {
              appendChatText(streamBuffer.slice(0, controlStart));
              streamBuffer = streamBuffer.slice(controlStart);
            }
            const jsonText = tryExtractJsonObject(streamBuffer, 0);
            if (!jsonText) return;
            const res = tryConsumeControl(jsonText);
            if (!res.consumed) appendChatText(jsonText);
            streamBuffer = streamBuffer.slice(jsonText.length);
            if (streamBuffer.startsWith("\n"))
              streamBuffer = streamBuffer.slice(1);
          }
        };
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          const chunk = decoder.decode(value, { stream: true });
          if (controlBufRef.current) {
            controlBufRef.current += chunk;
            if (controlBufRef.current.includes("\n")) {
              const buffered = controlBufRef.current;
              controlBufRef.current = "";
              processChunk(buffered);
            }
            continue;
          }
          processChunk(chunk);
        }
        const tail = decoder.decode();
        if (tail || controlBufRef.current) {
          const final = (controlBufRef.current || "") + (tail || "");
          controlBufRef.current = "";
          processChunk(final);
        }
        if (streamBuffer) {
          appendChatText(streamBuffer);
          streamBuffer = "";
        }

        const finishedAt = new Date().toISOString();
        const finalReply = chatOutput.trim();
        const finalSteps = researchStepsRef.current;
        if (finalSteps.length > 0 || finalReply) {
          setChatHistory((prev) => [
            ...prev,
            ...(finalSteps.length > 0
              ? [{ role: "status", steps: finalSteps, timestamp: finishedAt }]
              : []),
            ...(finalReply
              ? [
                  {
                    role: "assistant",
                    content: finalReply,
                    timestamp: finishedAt,
                  },
                ]
              : []),
          ]);
        }
      } catch (err) {
        if (err.name !== "AbortError") {
          console.error("Report chat error:", err);
          const messageText = err.message || "Report chat failed.";
          setReportError(messageText);
          setChatHistory((prev) => [
            ...prev,
            {
              role: "error",
              content: messageText,
              timestamp: new Date().toISOString(),
            },
          ]);
        }
      } finally {
        if (abortRef.current === controller) {
          setIsStreaming(false);
          setStatusText("");
          setStreamingReply("");
          streamingContentRef.current = "";
          setStreamingReportContent("");
          abortRef.current = null;
        }
      }
    },
    [
      reportId,
      reportContent,
      isStreaming,
      tryConsumeControl,
      currentCompany?.target,
      setIsRightOpen,
    ],
  );

  const handleStop = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    if (fullReplaceAnimRef.current) {
      clearInterval(fullReplaceAnimRef.current);
      fullReplaceAnimRef.current = null;
    }
    setIsStreaming(false);
    streamingContentRef.current = "";
    setStreamingReportContent("");
    setStreamingReply("");
    setStatusText("");
  }, []);

  const applyReportSnapshot = useCallback((doc) => {
    const nextContent = doc?.report_content || "";
    setReportTitle(doc?.title || "Untitled Report");
    setReportContent(nextContent);
    setReportCitations(doc?.report_citations || []);
    if (doc?.report_options) setReportOptions(doc.report_options);
    setChatHistory(Array.isArray(doc?.chat_history) ? doc.chat_history : []);
    setCanUndo(Boolean(doc?.can_undo));
    setCanRedo(Boolean(doc?.can_redo));
    if (Number.isFinite(Number(doc?.revision)))
      setRevision(Number(doc.revision));
    setHasSubmitted(nextContent.trim().length > 0);
    setStreamingReply("");
    setStreamingReportContent("");
    streamingContentRef.current = "";
    setStatusText("");
    setReportError("");
  }, []);

  const handleUndo = useCallback(async () => {
    if (!reportId || isStreaming || isUndoingReport || !canUndo) return;
    setIsUndoingReport(true);
    setReportError("");
    try {
      const res = await undoReport(reportId);
      const doc = res?.data || {};
      if (!res || doc.error) {
        setReportError(
          reportMutationError(res, doc, "Unable to undo report change."),
        );
        return;
      }
      setSelectedEdit(null);
      setEditSuggestion(null);
      applyReportSnapshot(doc);
      window.dispatchEvent(new CustomEvent("sidebar:refresh-reports"));
    } finally {
      setIsUndoingReport(false);
    }
  }, [applyReportSnapshot, canUndo, isStreaming, isUndoingReport, reportId]);

  const handleRedo = useCallback(async () => {
    if (!reportId || isStreaming || isRedoingReport || !canRedo) return;
    setIsRedoingReport(true);
    setReportError("");
    try {
      const res = await redoReport(reportId);
      const doc = res?.data || {};
      if (!res || doc.error) {
        setReportError(
          reportMutationError(res, doc, "Unable to redo report change."),
        );
        return;
      }
      setSelectedEdit(null);
      setEditSuggestion(null);
      applyReportSnapshot(doc);
      window.dispatchEvent(new CustomEvent("sidebar:refresh-reports"));
    } finally {
      setIsRedoingReport(false);
    }
  }, [applyReportSnapshot, canRedo, isRedoingReport, isStreaming, reportId]);

  const handleTextSelection = useCallback((selection) => {
    if (!selection?.text) {
      setSelectedEdit(null);
      setEditSuggestion(null);
      return;
    }
    setSelectedEdit(selection);
    setEditSuggestion(null);
    setReportError("");
  }, []);

  const handlePreviewEdit = useCallback(
    async (instruction) => {
      if (!reportId || !selectedEdit || isPreviewingEdit) return;
      if (!instruction?.trim()) {
        setReportError("Add edit instructions before previewing.");
        return;
      }
      if (selectedEdit.mapped === false) {
        setReportError(
          "This selection could not be mapped safely to the report markdown. Select a smaller plain-text passage and try again.",
        );
        return;
      }
      setIsPreviewingEdit(true);
      setReportError("");
      try {
        const res = await previewReportEdit({
          reportId,
          revision,
          selection: selectedEdit,
          instruction: instruction.trim(),
        });
        const data = res?.data || {};
        if (!res || data.error) {
          setReportError(
            reportMutationError(res, data, "Unable to preview edit."),
          );
          return;
        }
        setEditSuggestion(data);
      } finally {
        setIsPreviewingEdit(false);
      }
    },
    [isPreviewingEdit, reportId, revision, selectedEdit],
  );

  const handleDeleteSelection = useCallback(async () => {
    if (!reportId || !selectedEdit || isPreviewingEdit) return;
    if (selectedEdit.mapped === false) {
      setReportError(
        "This selection could not be mapped safely to the report markdown. Select a smaller plain-text passage and try again.",
      );
      return;
    }
    setIsPreviewingEdit(true);
    setReportError("");
    try {
      const res = await previewReportEdit({
        reportId,
        revision,
        selection: selectedEdit,
        instruction: "Delete the selected text.",
        op: "delete_range",
      });
      const data = res?.data || {};
      if (!res || data.error) {
        setReportError(
          reportMutationError(res, data, "Unable to preview deletion."),
        );
        return;
      }
      setEditSuggestion(data);
    } finally {
      setIsPreviewingEdit(false);
    }
  }, [isPreviewingEdit, reportId, revision, selectedEdit]);

  const handleSourceAwareEdit = useCallback(
    async (mode) => {
      if (!reportId || !selectedEdit || isPreviewingEdit || isStreaming) return;
      if (selectedEdit.mapped === false) {
        setReportError(
          "This selection could not be mapped safely to the report markdown. Select a smaller plain-text passage and try again.",
        );
        return;
      }
      const sourceMode =
        mode === "citation" ? "improve_citations" : "find_sources";
      const instruction =
        sourceMode === "improve_citations"
          ? "Automatically find and use the strongest relevant sources to improve citation support for the selected text. Rewrite only this selected range."
          : "Automatically find credible additional sources and use the strongest relevant ones to improve the selected text. Rewrite only this selected range.";
      setIsPreviewingEdit(true);
      setReportError("");
      try {
        const res = await previewReportEdit({
          reportId,
          revision,
          selection: selectedEdit,
          instruction,
          sourceMode,
          target: currentCompany?.target,
        });
        const data = res?.data || {};
        if (!res || data.error) {
          setReportError(
            reportMutationError(
              res,
              data,
              "Unable to preview source-backed edit.",
            ),
          );
          return;
        }
        setEditSuggestion(data);
      } finally {
        setIsPreviewingEdit(false);
      }
    },
    [
      currentCompany?.target,
      isPreviewingEdit,
      isStreaming,
      reportId,
      revision,
      selectedEdit,
    ],
  );

  const handleApplyEdit = useCallback(async () => {
    if (!reportId || !editSuggestion || isApplyingEdit) return;
    setIsApplyingEdit(true);
    setReportError("");
    try {
      const res = await applyReportEdit({
        reportId,
        editId: editSuggestion.edit_id,
        baseRevision: editSuggestion.base_revision,
      });
      const data = res?.data || {};
      if (!res || data.error) {
        setReportError(reportMutationError(res, data, "Unable to apply edit."));
        return;
      }
      setReportTitle(data.title || reportTitle || "Untitled Report");
      setReportContent(data.report_content || "");
      setReportCitations(data.report_citations || []);
      if (Number.isFinite(Number(data.revision)))
        setRevision(Number(data.revision));
      setCanUndo(true);
      setCanRedo(false);
      setHasSubmitted(Boolean((data.report_content || "").trim()));
      setChatHistory((prev) => [
        ...prev,
        {
          role: "system",
          type: "edit_applied",
          summary: editSuggestion.summary || "Applied highlighted edit.",
          edit_id: editSuggestion.edit_id,
          revision: data.revision,
          timestamp: new Date().toISOString(),
        },
      ]);
      setSelectedEdit(null);
      setEditSuggestion(null);
      window.getSelection?.()?.removeAllRanges?.();
      window.dispatchEvent(new CustomEvent("sidebar:refresh-reports"));
    } finally {
      setIsApplyingEdit(false);
    }
  }, [editSuggestion, isApplyingEdit, reportId, reportTitle]);

  const handleRejectEdit = useCallback(async () => {
    if (!reportId || !editSuggestion || isRejectingEdit) return;
    setIsRejectingEdit(true);
    setReportError("");
    try {
      const res = await rejectReportEdit({
        reportId,
        editId: editSuggestion.edit_id,
      });
      const data = res?.data || {};
      if (!res || data.error) {
        setReportError(
          reportMutationError(res, data, "Unable to reject edit."),
        );
        return;
      }
      setChatHistory((prev) => [
        ...prev,
        {
          role: "system",
          type: "edit_rejected",
          summary: "Rejected highlighted edit suggestion.",
          edit_id: editSuggestion.edit_id,
          timestamp: new Date().toISOString(),
        },
      ]);
      setSelectedEdit(null);
      setEditSuggestion(null);
      window.getSelection?.()?.removeAllRanges?.();
    } finally {
      setIsRejectingEdit(false);
    }
  }, [editSuggestion, isRejectingEdit, reportId]);

  const handleClearSelectedEdit = useCallback(() => {
    setSelectedEdit(null);
    setEditSuggestion(null);
    window.getSelection?.()?.removeAllRanges?.();
  }, []);

  const hasDraftedReport = reportContent && reportContent.trim().length > 0;

  const handleExportPdf = useCallback(() => {
    const el = document.querySelector("[data-report-content]");
    if (!el) return;
    const html = buildReportPrintHtml({
      title: reportTitle,
      contentElement: el,
      citations: reportCitations,
      generatedAt: new Date(),
      companyName: currentCompany?.case,
    });
    const printWindow = window.open("", "_blank");
    if (printWindow) {
      printWindow.document.write(html);
      printWindow.document.close();
      printWindow.document.title = `${reportTitle} - ${new Date().toLocaleDateString()}`;
      setTimeout(() => printWindow.print(), 250);
    }
  }, [currentCompany?.case, reportCitations, reportTitle]);

  const activeReportId = reportId || "default";

  if (activeReportId === "default") {
    if (isII) return <DefaultReportViewII />;
    else {
      return (
        <DefaultReportView
          key={
            currentCompany?.target || currentCompany?.case || "default-report"
          }
          target={currentCompany?.target}
          companyName={currentCompany?.case}
        />
      );
    }
  }

  if (isLoadingReport) {
    return <LoadingPanel message="Loading report…" />;
  }

  const hasReport = hasDraftedReport;
  const showInput = !hasReport && !isStreaming && !hasSubmitted;

  if (showInput) return <ReportInput onSubmit={handleSend} />;

  if (!hasReport && !streamingReportContent) {
    return (
      <LoadingPanel
        message={statusText || "Generating report…"}
        onStop={isStreaming ? handleStop : null}
      />
    );
  }

  return (
    <div className="flex h-full min-h-0 bg-mainBg pt-14">
      <section className="relative min-w-0 flex-1">
        <ReportDocument
          title={reportTitle}
          content={reportContent}
          citations={reportCitations}
          streamingContent={streamingReportContent}
          isStreaming={isStreaming}
          isUndoing={false}
          undoTargetContent=""
          undoTargetTitle=""
          undoTargetCitations={[]}
          onUndoComplete={() => {}}
          reportOptions={reportOptions}
          onSourceClick={handleCitationClick}
          activeSourceId={activeCitationUrl}
          citationInteractions={citationInteractions}
          selectedEdit={selectedEdit}
          onTextSelection={handleTextSelection}
        />

        {hasReport && (
          <div
            data-report-ui="true"
            className="pointer-events-none absolute left-6 right-6 top-4 z-20 flex items-center justify-end"
          >
            <div className="pointer-events-auto flex items-center gap-2">
              <button
                type="button"
                onClick={handleUndo}
                disabled={
                  !canUndo || isStreaming || isUndoingReport || isRedoingReport
                }
                className="inline-flex items-center gap-1.5 rounded-lg border border-surfaceBorder/70 bg-transparent px-3 py-1.5 text-[11px] font-medium text-mutedText backdrop-blur-sm transition-colors hover:bg-mainButtonBg/35 hover:text-mainText disabled:cursor-not-allowed disabled:opacity-45"
              >
                {isUndoingReport ? (
                  <Loader2 className="h-3 w-3 animate-spin" strokeWidth={2} />
                ) : (
                  <Undo2 className="h-3 w-3" strokeWidth={2} />
                )}
                Undo
              </button>
              <button
                type="button"
                onClick={handleRedo}
                disabled={
                  !canRedo || isStreaming || isUndoingReport || isRedoingReport
                }
                className="inline-flex items-center gap-1.5 rounded-lg border border-surfaceBorder/70 bg-transparent px-3 py-1.5 text-[11px] font-medium text-mutedText backdrop-blur-sm transition-colors hover:bg-mainButtonBg/35 hover:text-mainText disabled:cursor-not-allowed disabled:opacity-45"
              >
                {isRedoingReport ? (
                  <Loader2 className="h-3 w-3 animate-spin" strokeWidth={2} />
                ) : (
                  <Redo2 className="h-3 w-3" strokeWidth={2} />
                )}
                Redo
              </button>
              {!isStreaming && (
                <button
                  type="button"
                  onClick={handleExportPdf}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-surfaceBorder/70 bg-transparent px-3 py-1.5 text-[11px] font-medium text-mutedText backdrop-blur-sm transition-colors hover:bg-mainButtonBg/35 hover:text-mainText"
                >
                  <FileDown className="h-3 w-3" strokeWidth={2} />
                  Export PDF
                </button>
              )}
            </div>
          </div>
        )}

        <ReportEditSelectionModal
          selection={selectedEdit}
          editSuggestion={editSuggestion}
          isPreviewingEdit={isPreviewingEdit}
          isApplyingEdit={isApplyingEdit}
          error={reportError}
          isRejectingEdit={isRejectingEdit}
          onPreviewEdit={handlePreviewEdit}
          onDeleteSelection={handleDeleteSelection}
          onApplyEdit={handleApplyEdit}
          onRejectEdit={handleRejectEdit}
          onClearSelection={handleClearSelectedEdit}
          onSourceAwareEdit={handleSourceAwareEdit}
        />

        {isStreaming && (
          <div className="absolute bottom-4 left-1/2 z-20 flex -translate-x-1/2 items-center gap-2.5 rounded-full border border-surfaceBorder bg-mainBg/95 px-4 py-2 shadow-md backdrop-blur-sm">
            <Loader2
              className="h-3 w-3 animate-spin text-mutedText"
              strokeWidth={2}
            />
            <span className="text-[11.5px] font-medium text-mainText">
              {statusText || "Working…"}
            </span>
            <button
              onClick={handleStop}
              className="flex items-center gap-1 rounded-md border border-surfaceBorder px-2 py-0.5 text-[10.5px] font-medium text-mutedText transition-colors hover:bg-mainButtonBg/50 hover:text-mainText"
            >
              <Square className="h-2.5 w-2.5" strokeWidth={2.5} />
              Stop
            </button>
          </div>
        )}
      </section>
    </div>
  );
};

export default MainReports;
